import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { keyCanRun } from "@/components/epic-requests/run-with-choice";
import { useAiSettings, useStartKeyInterview } from "@/hooks/queries/use-ai-settings";
import { useCreateInterview } from "@/hooks/queries/use-interviews";
import { useEnqueueJob, useMachines } from "@/hooks/queries/use-mailbox";
import { ApiError } from "@/lib/api/client";
import { machineReadiness, mailboxApi } from "@/lib/api/mailbox";
import { cn } from "@/lib/utils";

/**
 * Starts the Idea helper on a saved idea, so the conversation lives in the
 * idea page's chat (never on a separate page). Used by New idea right after it
 * saves the idea, and by an existing idea's "Discuss in the chat".
 */

export const KEY_RUNNER = "key";

/** Where the Idea helper can run: a ready computer, or CompBox on the AI key. */
export function useIdeaHelperRunners() {
  const { data: machines } = useMachines();
  const { data: aiSettings } = useAiSettings();
  const ready = (machines ?? []).filter((m) => machineReadiness(m, Date.now(), "epic_request") === "ready");
  const keyReady = keyCanRun(aiSettings, "interview", (machines ?? []).length > 0);
  return [
    ...ready.map((m) => ({ value: m.id, label: `My computer: ${m.name}` })),
    ...(keyReady ? [{ value: KEY_RUNNER, label: "CompBox, with the AI key" }] : []),
  ];
}

/** Starts the Idea helper on `epicRequestId` and returns the conversation's job id. */
export function useStartIdeaHelper() {
  const queryClient = useQueryClient();
  const startKeyInterview = useStartKeyInterview();
  const enqueueJob = useEnqueueJob();
  const createInterview = useCreateInterview();
  return async (input: { epicRequestId: string; runner: string; firstMessage?: string }): Promise<string> => {
    let jobId: string;
    if (input.runner === KEY_RUNNER) {
      jobId = (await startKeyInterview.mutateAsync({ epicRequestId: input.epicRequestId })).data.id;
    } else {
      jobId = (await enqueueJob.mutateAsync({ machineId: input.runner, type: "epic_request", payload: {} })).id;
      // Links the conversation to the idea, so its saves update this idea in place.
      await createInterview.mutateAsync({ jobId, epicRequestId: input.epicRequestId });
    }
    if (input.firstMessage) await mailboxApi.postTurn(jobId, { content: input.firstMessage });
    await queryClient.invalidateQueries({ queryKey: ["epicRequests"] });
    return jobId;
  };
}

/** "Discuss in the chat" on an idea with no conversation yet. */
export function StartIdeaHelper({ epicRequestId, onStarted }: { epicRequestId: string; onStarted: () => void }) {
  const runners = useIdeaHelperRunners();
  const start = useStartIdeaHelper();
  const [choice, setChoice] = useState("");
  const runner = runners.some((r) => r.value === choice) ? choice : (runners[0]?.value ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (runners.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        To talk it through, open CompBox on one of your computers or add an AI key in Settings.
      </p>
    );
  }
  return (
    <div className="mt-1 flex flex-col gap-2">
      {runners.length > 1 && (
        <select
          aria-label="Where the Idea helper runs"
          value={runner}
          onChange={(e) => {
            setChoice(e.target.value);
          }}
          className="min-h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
        >
          {runners.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          start({ epicRequestId, runner })
            .then(onStarted)
            .catch((e: unknown) => {
              setError(e instanceof ApiError ? e.message : "Couldn't start the conversation. Try again.");
            })
            .finally(() => {
              setBusy(false);
            });
        }}
        className={cn(
          "inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90",
          busy && "opacity-60"
        )}
      >
        {busy ? "Starting…" : "Discuss in the chat"}
      </button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
